/**
 * 任务详情抽屉：编辑主字段 + 子任务/标签/提醒管理。
 * 请求体严格按 UpdateTaskBody 白名单逐项拼装。
 */
import { useEffect, useState } from 'react';
import {
  useAddReminder,
  useAddSubtask,
  useAreas,
  useAttachTag,
  useCancelTask,
  useCompleteTask,
  useDeleteReminder,
  useDeleteSubtask,
  useDeleteTask,
  useDetachTag,
  useProjects,
  useTask,
  useUpdateSubtask,
  useUpdateTask,
} from '../hooks/useTasks';
import type { Priority, UpdateTaskBody } from '../lib/types';
import { PRIORITY_LABELS, PRIORITY_OPTIONS, STATUS_LABELS, dateTimeStr } from '../lib/utils';
import { Badge, Button, ConfirmDialog, Drawer, ErrorBox, Field, Input, LoadingBlock, Select, Textarea, Toggle, errMsg } from './ui';
import { Icon } from './icons';

interface TaskDrawerProps {
  taskId: string | null;
  onClose: () => void;
}

const emptyEdit = {
  title: '',
  notes: '',
  dueDate: '',
  dueTime: '',
  priority: '',
  estimateMinutes: '',
  location: '',
  recurrence: '',
  areaId: '',
  projectId: '',
  contactId: '',
};

export function TaskDrawer({ taskId, onClose }: TaskDrawerProps) {
  const { data: task, isLoading } = useTask(taskId ?? undefined);
  const { data: areas } = useAreas();
  const { data: projects } = useProjects({ pageSize: 100 });
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const completeTask = useCompleteTask();
  const cancelTask = useCancelTask();
  const addSubtask = useAddSubtask();
  const updateSubtask = useUpdateSubtask();
  const deleteSubtask = useDeleteSubtask();
  const attachTag = useAttachTag();
  const detachTag = useDetachTag();
  const addReminder = useAddReminder();
  const deleteReminder = useDeleteReminder();

  const [edit, setEdit] = useState(emptyEdit);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newSubtask, setNewSubtask] = useState('');
  const [newTag, setNewTag] = useState('');
  const [reminderAt, setReminderAt] = useState('');

  // 任务载入后回填表单
  useEffect(() => {
    if (task) {
      setEdit({
        title: task.title,
        notes: task.notes ?? '',
        dueDate: task.dueDate ?? '',
        dueTime: task.dueTime ?? '',
        priority: task.priority ? String(task.priority) : '',
        estimateMinutes: task.estimateMinutes ? String(task.estimateMinutes) : '',
        location: task.location ?? '',
        recurrence: task.recurrence ?? '',
        areaId: task.areaId ?? '',
        projectId: task.projectId ?? '',
        contactId: task.contactId ?? '',
      });
      setDirty(false);
      setError(null);
    }
  }, [task]);

  const set = (patch: Partial<typeof emptyEdit>) => {
    setEdit((f) => ({ ...f, ...patch }));
    setDirty(true);
  };

  async function save() {
    if (!taskId) return;
    if (!edit.title.trim()) {
      setError('请填写任务标题');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: UpdateTaskBody = { title: edit.title.trim() };
      body.notes = edit.notes.trim() || null;
      body.dueDate = edit.dueDate || null;
      body.dueTime = edit.dueTime || null;
      body.priority = edit.priority ? (Number(edit.priority) as Priority) : null;
      body.estimateMinutes = edit.estimateMinutes ? Number(edit.estimateMinutes) : null;
      body.location = edit.location.trim() || null;
      body.recurrence = edit.recurrence.trim() || null;
      body.areaId = edit.areaId || null;
      body.projectId = edit.projectId || null;
      body.contactId = edit.contactId || null;
      await updateTask.mutateAsync({ id: taskId, body });
      setDirty(false);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <Drawer
      open={Boolean(taskId)}
      onClose={onClose}
      title="任务详情"
      footer={
        <>
          {dirty && (
            <Button variant="secondary" onClick={save} loading={saving}>
              保存修改
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            关闭
          </Button>
        </>
      }
    >
      {isLoading || !task ? (
        <LoadingBlock text="加载任务…" />
      ) : (
        <div className="space-y-6">
          <ErrorBox error={error} />

          {/* 状态与快捷操作 */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {STATUS_LABELS[task.status]}
            </Badge>
            {(task.status === 'TODO' || task.status === 'DOING') && (
              <>
                <Button
                  size="sm"
                  onClick={() => run(() => completeTask.mutateAsync(task.id))}
                >
                  <Icon name="check" className="mr-1 h-4 w-4" />
                  完成
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => run(() => cancelTask.mutateAsync(task.id))}
                >
                  取消任务
                </Button>
              </>
            )}
            <div className="flex-1" />
            <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>
              <Icon name="trash" className="mr-1 h-4 w-4" />
              删除
            </Button>
          </div>

          {/* 主字段编辑 */}
          <div className="space-y-4">
            <Field label="标题" required>
              <Input value={edit.title} onChange={(e) => set({ title: e.target.value })} />
            </Field>
            <Field label="备注">
              <Textarea rows={3} value={edit.notes} onChange={(e) => set({ notes: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="截止日期">
                <Input type="date" value={edit.dueDate} onChange={(e) => set({ dueDate: e.target.value })} />
              </Field>
              <Field label="截止时间">
                <Input type="time" value={edit.dueTime} onChange={(e) => set({ dueTime: e.target.value })} />
              </Field>
              <Field label="优先级">
                <Select value={edit.priority} onChange={(e) => set({ priority: e.target.value })}>
                  <option value="">无</option>
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_LABELS[p]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="预估（分钟）">
                <Input
                  type="number"
                  min={0}
                  value={edit.estimateMinutes}
                  onChange={(e) => set({ estimateMinutes: e.target.value })}
                />
              </Field>
              <Field label="领域">
                <Select value={edit.areaId} onChange={(e) => set({ areaId: e.target.value })}>
                  <option value="">未分类</option>
                  {(areas ?? []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="项目">
                <Select value={edit.projectId} onChange={(e) => set({ projectId: e.target.value })}>
                  <option value="">无</option>
                  {(projects?.items ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="地点">
                <Input value={edit.location} onChange={(e) => set({ location: e.target.value })} />
              </Field>
              <Field label="重复规则 (RRULE)">
                <Input
                  value={edit.recurrence}
                  onChange={(e) => set({ recurrence: e.target.value })}
                  placeholder="如 FREQ=DAILY"
                />
              </Field>
            </div>
          </div>

          {/* 子任务 */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
              子任务 ({task.subtasks.length})
            </h3>
            <div className="space-y-1.5">
              {task.subtasks.map((s) => (
                <div key={s.id} className="flex items-center gap-2">
                  <Toggle
                    checked={s.done}
                    onChange={(done) =>
                      run(() => updateSubtask.mutateAsync({ taskId: task.id, sid: s.id, body: { done } }))
                    }
                  />
                  <span className={`flex-1 text-sm ${s.done ? 'text-slate-400 line-through' : ''}`}>{s.title}</span>
                  <button
                    type="button"
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600 dark:hover:bg-slate-800"
                    onClick={() => run(() => deleteSubtask.mutateAsync({ taskId: task.id, sid: s.id }))}
                    aria-label="删除子任务"
                  >
                    <Icon name="close" className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  value={newSubtask}
                  onChange={(e) => setNewSubtask(e.target.value)}
                  placeholder="添加子任务"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newSubtask.trim()) {
                      run(() => addSubtask.mutateAsync({ taskId: task.id, title: newSubtask.trim() }));
                      setNewSubtask('');
                    }
                  }}
                />
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!newSubtask.trim()}
                  onClick={() => {
                    run(() => addSubtask.mutateAsync({ taskId: task.id, title: newSubtask.trim() }));
                    setNewSubtask('');
                  }}
                >
                  添加
                </Button>
              </div>
            </div>
          </section>

          {/* 标签 */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">标签</h3>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {task.tags.map((t) => (
                <span
                  key={t.id}
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
                >
                  #{t.name}
                  <button
                    type="button"
                    onClick={() => run(() => detachTag.mutateAsync({ taskId: task.id, name: t.name }))}
                    aria-label={`移除标签 ${t.name}`}
                  >
                    <Icon name="close" className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {task.tags.length === 0 && <span className="text-xs text-slate-400">暂无标签</span>}
            </div>
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="标签名"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newTag.trim()) {
                    run(() => attachTag.mutateAsync({ taskId: task.id, name: newTag.trim() }));
                    setNewTag('');
                  }
                }}
              />
              <Button
                size="sm"
                variant="secondary"
                disabled={!newTag.trim()}
                onClick={() => {
                  run(() => attachTag.mutateAsync({ taskId: task.id, name: newTag.trim() }));
                  setNewTag('');
                }}
              >
                添加
              </Button>
            </div>
          </section>

          {/* 提醒 */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
              提醒 ({task.reminders.length})
            </h3>
            <div className="space-y-1.5">
              {task.reminders.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-sm">
                  <Icon name="bell" className="h-4 w-4 text-amber-500" />
                  <span className="flex-1">{dateTimeStr(r.remindAt)}</span>
                  <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">{r.channel}</Badge>
                  <button
                    type="button"
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600 dark:hover:bg-slate-800"
                    onClick={() => run(() => deleteReminder.mutateAsync({ taskId: task.id, rid: r.id }))}
                    aria-label="删除提醒"
                  >
                    <Icon name="close" className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input type="datetime-local" value={reminderAt} onChange={(e) => setReminderAt(e.target.value)} />
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!reminderAt}
                  onClick={() => {
                    run(() =>
                      addReminder.mutateAsync({
                        taskId: task.id,
                        remindAt: new Date(reminderAt).toISOString(),
                        channel: 'INAPP',
                      }),
                    );
                    setReminderAt('');
                  }}
                >
                  添加
                </Button>
              </div>
            </div>
          </section>

          <ConfirmDialog
            open={confirmDelete}
            title="删除任务"
            message="删除后不可恢复，确定删除该任务吗？"
            confirmText="删除"
            danger
            onConfirm={async () => {
              await run(() => deleteTask.mutateAsync(task.id));
              setConfirmDelete(false);
              onClose();
            }}
            onCancel={() => setConfirmDelete(false)}
          />
        </div>
      )}
    </Drawer>
  );
}
